/**
 * Autenticação mock — access em memória da sessão da app; refresh válidos até logout com persistência dedicada ao mock (F5).
 * Contratos: docs/contracts/api/auth/post-auth-login.md, post-auth-refresh.md, post-auth-logout.md, post-auth-change-password.md
 */
import type { AdminMockRequestHandler } from './mock-api-admin.handlers';

import {
  mockAuthRefreshTokenActive,
  mockAuthRefreshTokenRegister,
  mockAuthRefreshTokenRevoke,
  mockAuthRefreshTokenRotate,
} from './mock-api-auth-refresh-token-store';

import { of } from 'rxjs';

import { HttpRequest, HttpResponse } from '@angular/common/http';

function ok<T>(data: T) {
  return { errors: [] as { code: string; messageKey: string }[], data, success: true };
}

/** Quando true, login devolve user.mustChangePassword true até primeira troca de senha bem-sucedida. */
let forceMustChangePasswordOnNextLogin = false;

function isoFuture(ms: number): string {
  return new Date(Date.now() + ms).toISOString();
}

function mockUser(email: string) {
  return {
    displayName: 'Antonio Carlos A. da Fonseca',
    email,
    id: '00000000-0000-4000-8000-000000000001',
    mustChangePassword: forceMustChangePasswordOnNextLogin,
    platformRole: 'Admin' as const,
  };
}

function parseJsonBody(req: HttpRequest<unknown>): Record<string, unknown> | null {
  const body = req.body;

  if (body == null)
    return null;

  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body) as unknown;

      if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed))
        return null;

      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  if (typeof body !== 'object' || Array.isArray(body))
    return null;

  return body as Record<string, unknown>;
}

function bearerAccess(req: HttpRequest<unknown>): string | null {
  const h = req.headers.get('Authorization');

  if (h == null || !h.startsWith('Bearer '))
    return null;

  return h.slice('Bearer '.length).trim();
}

export const mockApiAuthHandlers: AdminMockRequestHandler[] = [
  {
    method: 'POST',
    path: /^\/api\/auth\/login$/,
    handle: (req) => {
      const body = parseJsonBody(req);
      const email = typeof body?.['email'] === 'string' ? body['email'].trim() : '';
      const password = typeof body?.['password'] === 'string' ? body['password'] : '';
      const rememberMe = body?.['rememberMe'] === true;

      if (!email || !password) {
        return of(
          new HttpResponse({
            body: {
              data: null,
              errors: [{ code: 'Validation.Failed', messageKey: 'app.validation' }],
              success: false,
            },
            status: 400,
          }),
        );
      }

      const accessToken = `mock-access-${globalThis.crypto.randomUUID()}`;
      const refreshToken = `mock-refresh-${globalThis.crypto.randomUUID()}`;
      mockAuthRefreshTokenRegister(refreshToken);

      const refreshTtlMs = rememberMe ? 30 * 86_400_000 : 86_400_000;

      const data = {
        accessToken,
        accessTokenExpiresAtUtc: isoFuture(15 * 60_000),
        refreshToken,
        refreshTokenExpiresAtUtc: isoFuture(refreshTtlMs),
        user: mockUser(email),
      };

      return of(
        new HttpResponse({
          body: ok(data),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'POST',
    path: /^\/api\/auth\/refresh$/,
    handle: (req) => {
      const body = parseJsonBody(req);
      const refreshToken = typeof body?.['refreshToken'] === 'string' ? body['refreshToken'] : '';

      if (!refreshToken || !mockAuthRefreshTokenActive(refreshToken)) {
        return of(
          new HttpResponse({
            body: {
              data: null,
              errors: [{ code: 'Auth.InvalidRefreshToken', messageKey: 'app.auth.errors.unauthorized' }],
              success: false,
            },
            status: 401,
          }),
        );
      }

      const newRefresh = `mock-refresh-${globalThis.crypto.randomUUID()}`;
      mockAuthRefreshTokenRotate(refreshToken, newRefresh);
      const email = 'mock@example.com';

      const data = {
        accessToken: `mock-access-${globalThis.crypto.randomUUID()}`,
        accessTokenExpiresAtUtc: isoFuture(15 * 60_000),
        refreshToken: newRefresh,
        refreshTokenExpiresAtUtc: isoFuture(86_400_000),
        user: mockUser(email),
      };

      return of(
        new HttpResponse({
          body: ok(data),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'POST',
    path: /^\/api\/auth\/logout$/,
    handle: (req) => {
      const body = parseJsonBody(req);
      const refreshToken = typeof body?.['refreshToken'] === 'string' ? body['refreshToken'] : '';

      if (refreshToken)
        mockAuthRefreshTokenRevoke(refreshToken);

      return of(
        new HttpResponse({
          body: ok(null),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'POST',
    path: /^\/api\/auth\/change-password$/,
    handle: (req) => {
      const token = bearerAccess(req);

      if (token == null || !token.startsWith('mock-access-')) {
        return of(
          new HttpResponse({
            body: {
              data: null,
              errors: [{ code: 'Auth.Unauthorized', messageKey: 'app.auth.errors.unauthorized' }],
              success: false,
            },
            status: 401,
          }),
        );
      }

      const body = parseJsonBody(req);
      const currentPassword = typeof body?.['currentPassword'] === 'string' ? body['currentPassword'] : '';
      const newPassword = typeof body?.['newPassword'] === 'string' ? body['newPassword'] : '';

      if (!currentPassword || !newPassword || newPassword.length < 8) {
        return of(
          new HttpResponse({
            body: {
              data: null,
              errors: [{ code: 'Validation.Failed', messageKey: 'app.validation' }],
              success: false,
            },
            status: 400,
          }),
        );
      }

      forceMustChangePasswordOnNextLogin = false;

      return of(
        new HttpResponse({
          body: ok({ success: true }),
          status: 200,
        }),
      );
    },
  },
];
